// =============================================================================
// "Is anything actually happening?" — one counter, for the whole app.
//
// Staff reported the app sitting on the word "Loading…" for fifteen seconds
// with nothing else to look at, and no way to tell a slow request from a dead
// one. Two separate problems, and this file is the shared half of both:
//
//   1. A request that is merely slow should say so — a spinner, not a noun.
//      See lib/loading.js.
//   2. A request that has stopped moving should offer a way out. Every data
//      call is funnelled through here (api.js wraps the whole facade), so the
//      shell can notice when one has been in flight too long and put a Retry
//      in front of the user rather than leaving them guessing.
//
// Deliberately not a request queue or a cache. It counts, and it times.
// =============================================================================

// A request in flight for longer than this has stopped being "slow" and has
// started being "stuck" as far as someone at a front desk is concerned.
export const STALL_MS = 12000;

let inFlight = 0;
let oldestStartedAt = 0;
const listeners = new Set();
let timer = null;

/** Notified with `true` when something has been stuck for STALL_MS, `false` when it clears. */
export function onStall(handler) {
  listeners.add(handler);
  return () => listeners.delete(handler);
}

let stalled = false;
function setStalled(next) {
  if (next === stalled) return;
  stalled = next;
  for (const h of listeners) h(stalled);
}

function tick() {
  if (!inFlight) { setStalled(false); return; }
  setStalled(Date.now() - oldestStartedAt >= STALL_MS);
}

function begin() {
  if (inFlight === 0) oldestStartedAt = Date.now();
  inFlight += 1;
  // One interval for the whole app, running only while something is pending.
  if (!timer) timer = setInterval(tick, 1000);
}

function end() {
  inFlight = Math.max(0, inFlight - 1);
  if (inFlight === 0) {
    clearInterval(timer); timer = null;
    setStalled(false);
  } else {
    // The oldest call finished but others remain. Restarting the clock here is
    // approximate — it can only ever delay the warning, never raise a false
    // one, which is the right way round for a message that interrupts.
    oldestStartedAt = Date.now();
  }
}

/** Wraps an async function so every call is counted. Errors still propagate. */
export function tracked(fn) {
  return async (...args) => {
    begin();
    try { return await fn(...args); } finally { end(); }
  };
}
